import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  CSV_UTC_OFFSET_HEADER,
  CsvCondition,
  csvUtcOffsetHeader,
  csvDateTime,
  csvFilename,
  csvUtcOffset,
  csvZoneLabel,
  downloadCsv,
  toCsv,
} from 'config/csv';

export interface ExportField {
  header: string;
  field: string;
  customExport?: (data: any) => string;
  /**
   * EV-116 段階2。業務時間帯で計算した日時の列（値は UTC の ISO 文字列）。見出しに時間帯を付ける。
   * 省略したときは customExport が csvDateTime の列を日時の列とみなす。
   */
  zoned?: boolean;
}

interface ExportProps {
  data: any[];
  exportFields: ExportField[]; // Array of fields and headers
  /** ADM-11-AC2。ファイル名の先頭（画面ごとの名前）。 */
  name?: string;
  /** ADM-11-AC2。出力した条件（期間・運転者・並び順・ページなど）。ファイル名に入れる。 */
  conditions?: CsvCondition[];
}

const getValueByPath = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const processField = (item: any, field: ExportField) => {
  if (field.customExport) {
    return field.customExport(getValueByPath(item, field.field));
  }
  return getValueByPath(item, field.field);
};

const isZoned = (field: ExportField) => field.zoned ?? field.customExport === csvDateTime;

/**
 * 記録の配列を CSV にしてダウンロードさせる。A28（2026-11-03）。エクスポートの範囲を選ぶ画面
 * （ExportRangeDialog）からも同じ形で出すため、ボタンの部品から切り出した。
 */
export function exportRowsToCsv(
  data: any[],
  exportFields: ExportField[],
  name: string,
  conditions: CsvCondition[]
): void {
  const now = new Date();
  // EV-116 段階2（2026-10-11 PM 決定6）。日時の列の見出しに時間帯（UTC との差）を付ける。
  // 出力する行の中で差が変わる（夏時間をまたぐ）ときは時間帯の名前を付け、行ごとの差の列を足す。
  const zonedFields = exportFields.filter(isZoned);
  const zone = csvZoneLabel(
    data.flatMap((item) => zonedFields.map((field) => getValueByPath(item, field.field))),
    now
  );
  const perRow = zone.perRow && zonedFields.length > 0;
  const headers = exportFields.map((f) => (isZoned(f) ? `${f.header} (${zone.label})` : f.header));
  // 日時の列が1つなら `UTC Offset` の1列。2つ以上なら列ごとに `<見出し> UTC Offset`（RV-276。
  // 1行の中で時刻ごとに差が違い得るので、最初の列の差だけでは足りない）。
  const offsetHeaders = !perRow
    ? []
    : zonedFields.length === 1
    ? [CSV_UTC_OFFSET_HEADER]
    : zonedFields.map((field) => csvUtcOffsetHeader(field.header));
  const rows = data.map((item) => {
    const row = exportFields.map((field) => processField(item, field));
    return perRow
      ? [...row, ...zonedFields.map((field) => csvUtcOffset(getValueByPath(item, field.field)))]
      : row;
  });
  // ADM-10。引用符・改行・数式の扱いは csv.ts に集約する。
  const csv = toCsv([...headers, ...offsetHeaders], rows);
  // ADM-11-AC2。件数と作成日時は押した時点のもの。
  downloadCsv(csvFilename(name, conditions, data.length, now), csv);
}

const Export: React.FC<ExportProps> = ({
  data,
  exportFields,
  name = 'export',
  conditions = [],
}) => {
  const { t } = useTranslation(['common']);
  const handleExport = () => exportRowsToCsv(data, exportFields, name, conditions);

  return (
    <button type="button"
      onClick={handleExport}
      className="bg-primary/50 text-primary/300 rounded-md border px-4 h-10 text-center whitespace-nowrap"
    >
      {t('export')}
    </button>
  );
};

export default Export;
